// PERFORMANCE & LOGIC ENHANCEMENT:
// This context has been refactored to handle server-side filtering and pagination.
// It no longer fetches all products at once. Instead, it queries the API
// with specific filters and page numbers, making the app scalable.

import React, { createContext, useState, useContext, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import { fetchProducts } from '../services/api';
import { API_BASE_URL } from '../config'; // Import API_BASE_URL for the background fetch

const FilterContext = createContext();

export const FilterProvider = ({ children }) => {
  // State for filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [currentPage, setCurrentPage] = useState(1); // Renamed from 'page'
  const [loadedProducts, setLoadedProducts] = useState([]); // To accumulate products for infinite scroll
  const [isAppendingProducts, setIsAppendingProducts] = useState(false); // For infinite scroll loading indicator
  const limit = 12; // Products per page
  const MAX_INFINITE_SCROLL_PRODUCTS = 100; // Define a soft limit for products loaded via infinite scroll

  // Debounce search term to avoid excessive API calls while the user is typing
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to the first page on a new search
    }, 300); // 300ms delay
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reset page to 1 when any other filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedBrand, minPrice, maxPrice, sortBy]);

  // PERFORMANCE OPTIMIZATION: useQuery now fetches paginated and filtered data from the server.
  // The query key is an array that includes all dependencies. react-query automatically
  // re-fetches when any part of this key changes.
  const { data: response, isLoading: queryLoading } = useQuery( // Renamed isLoading to queryLoading
    ['products', {
      page: currentPage, // Use currentPage
      limit,
      searchTerm: debouncedSearchTerm,
      category: selectedCategory,
      brand: selectedBrand,
      minPrice,
      maxPrice,
      sort: sortBy
    }],
    fetchProducts,
    {
      keepPreviousData: true,
      staleTime: 1000 * 60,
    }
  );

  const totalPages = response?.totalPages || 1;

  useEffect(() => {
    if (response?.products) {
      if (currentPage === 1) {
        setLoadedProducts(response.products);
      } else {
        setLoadedProducts(prevProducts => {
          // Filter out duplicates if any exist (e.g., due to keepPreviousData or network quirks)
          const newProducts = response.products.filter(
            newP => !prevProducts.some(p => p._id === newP._id)
          );
          // Combine and then truncate if exceeding MAX_INFINITE_SCROLL_PRODUCTS
          const combinedProducts = [...prevProducts, ...newProducts];
          return combinedProducts.slice(0, MAX_INFINITE_SCROLL_PRODUCTS);
        });
      }
    }
    // Also, handle the `isAppendingProducts` state here.
    // If queryLoading is false, and currentPage > 1, then we've finished appending.
    if (!queryLoading && currentPage > 1) {
      setIsAppendingProducts(false);
    }
  }, [response?.products, currentPage, queryLoading]); // Added queryLoading as dependency

  // Derived loading states
  const productsLoading = queryLoading && currentPage === 1; // Initial load indicator

  const loadNextPage = useCallback(() => {
    // Only load if there are more pages, not currently loading, AND
    // the current number of loaded products is less than the max limit
    if (currentPage < totalPages && !queryLoading && loadedProducts.length < MAX_INFINITE_SCROLL_PRODUCTS) {
      setIsAppendingProducts(true); // Set loading state for appending
      setCurrentPage(prevPage => prevPage + 1);
    }
  }, [currentPage, totalPages, queryLoading, loadedProducts.length]);

  // COMPROMISE: For the client-side recommendation engine to continue working,
  // we fetch a large list of products in the background with a low priority.
  // A proper long-term solution would be a dedicated backend recommendation API.
  const { data: allProductsResponse } = useQuery('allProductsForRecommendations', async () => {
    const res = await fetch(`${API_BASE_URL}/api/products?limit=500`);
    return res.json();
  }, {
    staleTime: Infinity, // Never consider this data stale
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const allProducts = allProductsResponse?.products || [];

  // Fetch all categories directly from the backend
  const { data: categoriesData } = useQuery('allCategories', async () => {
    const res = await fetch(`${API_BASE_URL}/api/categories`);
    if (!res.ok) {
      throw new Error('Failed to fetch categories');
    }
    return res.json();
  }, {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Categories and brands for the filter UI are derived from the background 'allProducts' list.
  const { categories, brands } = useMemo(() => {
    const fetchedCategories = categoriesData || []; // Categories now come directly
    const uniqueBrands = [...new Set(allProducts.map(p => p.brand).filter(Boolean))];
    return { categories: fetchedCategories, brands: uniqueBrands };
  }, [allProducts, categoriesData]);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedBrand("");
    setMinPrice("");
    setMaxPrice("");
    setSortBy("default");
    setCurrentPage(1);
  }, []);

  const toggleFilters = useCallback(() => setShowFilters(prev => !prev), []);

  const handlePriceChange = useCallback((type, value) => {
    if (type === 'min') setMinPrice(value);
    else setMaxPrice(value);
  }, []);

  const value = {
    searchTerm, setSearchTerm,
    selectedCategory, setSelectedCategory,
    selectedBrand, setSelectedBrand,
    minPrice, setMinPrice,
    maxPrice, setMaxPrice,
    showFilters, setShowFilters,
    sortBy, setSortBy,
    categories, brands,
    clearFilters, toggleFilters, handlePriceChange,
    filteredProducts: loadedProducts, // Now provides accumulated products
    productsLoading, // Initial loading state
    isAppendingProducts: queryLoading && currentPage > 1, // Derived appending state
    allProducts, // Still provided for recommendation engine
    currentPage,
    totalPages,
    loadNextPage, // New function for infinite scroll
    MAX_INFINITE_SCROLL_PRODUCTS, // Expose the max limit
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = () => useContext(FilterContext);
